import React, { useState, useEffect, useRef } from 'react';
import { IoVolumeMute, IoVolumeHigh } from 'react-icons/io5';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import TurnModal from '../components/TurnModal';
import SanqiStatusBoard from '../components/SanqiStatusBoard';
import './SanqiDeviceDetail.css';

function SanqiDeviceDetail(props) {
  const TURN_OPTIONS = [
    { value: 200, label: '200转' },
    { value: 500, label: '500转' },
    { value: 800, label: '800转' }
  ];
  // 路由和用户上下文
  const { user, socket } = useAuth();
  const authToken = user.token; // assume token is stored here
  const navigate = useNavigate();
  const { deviceId } = useParams();
  // 组件状态
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameSession, setGameSession] = useState(null);
  const [gameStatus, setGameStatus] = useState('idle');
  const [lastGameResult, setLastGameResult] = useState(null);
  const [selectedTurns, setSelectedTurns] = useState(TURN_OPTIONS[0].value);
  const [showTurnModal, setShowTurnModal] = useState(false);

  // 摇杆状态 - 只需要视觉效果
  const joystickRef = useRef(null);
  const playerRef = useRef(null);
  const consoleRef = useRef(null);
  const [consoleScale, setConsoleScale] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  // 视频加载就绪
  const [videoReady, setVideoReady] = useState(false);
  // 顶部状态栏缩放（不超过 20% 视口高度）
  const statusBaseRef = useRef(null);
  const [statusScale, setStatusScale] = useState(1);
  const [statusScaledH, setStatusScaledH] = useState(null);

  // 顶部状态栏：时钟文本（HH:MM:SS）
  const [clockText, setClockText] = useState(() => new Date().toLocaleTimeString('zh-CN', { hour12: false }));
  useEffect(() => {
    const t = setInterval(() => {
      setClockText(new Date().toLocaleTimeString('zh-CN', { hour12: false }));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // 按键状态跟踪 - 用于自动按下-松开逻辑
  const [pendingRelease, setPendingRelease] = useState(null); // 存储待松开的按键信息

  // 防止重复调用停止游戏的标志
  const [isStoppingGame, setIsStoppingGame] = useState(false);

  // 按键配置 - 定义哪些按键需要按下-松开逻辑
  const buttonConfig = {
    '###mxa': { // MAX按键需要按下-松开
      pressCommand: '###mxa',
      releaseCommand: '###mxb', 
      expectResponse: 'mxaok',
      label: 'MAX按键'
    },
    '###laa': { // 摇杆按键需要按下-松开
      pressCommand: '###laa',
      releaseCommand: '###lab', 
      expectResponse: 'laaok', // 摇杆反馈
      label: '摇杆按键'
    },
    '###lta': { // 1号按键需要按下-松开
      pressCommand: '###lta',
      releaseCommand: '###ltb', 
      expectResponse: 'ltaok', // 1号按键反馈
      label: '1号按键'
    },
    '###mea': { // 2号按键需要按下-松开
      pressCommand: '###mea',
      releaseCommand: '###meb', 
      expectResponse: 'meaok', // 2号按键反馈
      label: '2号按键'
    },
    '###rta': { // 3号按键需要按下-松开
      pressCommand: '###rta',
      releaseCommand: '###rtb', 
      expectResponse: 'rtaok', // 3号按键反馈
      label: '3号按键'
    }
  };
  // 按键视觉效果状态
  const [pressedButtons, setPressedButtons] = useState({
    max: false,
    joystick: false,
    button1: false,
    button2: false,
    button3: false
  });

  // 按键视觉效果函数
  const triggerButtonEffect = (buttonKey) => {
    setPressedButtons(prev => ({ ...prev, [buttonKey]: true }));
    setTimeout(() => {
      setPressedButtons(prev => ({ ...prev, [buttonKey]: false }));
    }, 200); // 200ms的按压效果
  };

  // 按键处理函数 - 每个按钮点击一次只发送一次命令
  const handleMaxPress = () => {
    // 需求：选择转数并开始后，再按“自动键”应作为“停止”，不再触发走转
    if (gameStatus === 'running' || gameSession?.status === 'running') {
      if (isStoppingGame) {
        console.log('[三七机] 正在停止中，忽略重复自动键');
        return;
      }
  console.log('[三七机] 自动键在运行中被点击 => 触发停止');
  handleStopGame();
      return;
    }
    // 未在运行：仍保留原自动键（MAX）单击效果
    triggerButtonEffect('max');
    sendCommand('###mxa', 'MAX按键');
  };
  const handleButton1Press = () => {
    triggerButtonEffect('button1');
    sendCommand('###lta', '1号按键');
  };
  const handleButton2Press = () => {
    triggerButtonEffect('button2');
    sendCommand('###mea', '2号按键');
  };
  const handleButton3Press = () => {
    triggerButtonEffect('button3');
    sendCommand('###rta', '3号按键');
  };

  // 按键命令发送函数（修正版，简化自动松开逻辑）
  async function sendCommand(command, label) {
    const config = buttonConfig[command] || {};
    try {
      if (!device?._id) {
        console.warn(`[${label}] 设备尚未就绪，忽略发送`);
        return;
      }
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
      // 改用手动按键接口：空闲时自动占用，避免“未被当前用户占用”报错
      const res = await api.post(
        '/api/player/sanqi/manual-button',
        { deviceId: device._id, command, step: 1, expectedResponse: config.expectResponse || 'ok' },
        { withCredentials: true, headers }
      );
      if (!res.data.success) { console.error(`${label} 发送失败:`, res.data.message); return; }
      if (config.releaseCommand) {
        // 待松开逻辑
        setPendingRelease({ pressCommand: command, releaseCommand: config.releaseCommand, expectResponse: config.expectResponse, label, timestamp: Date.now() });
      } else {
        console.log(`${label} 单次命令已发送`);
      }
    } catch (err) {
      console.error(`${label} 发送错误:`, err);
    }
  }

  // 计算剩余转数
  const remainingTurns = React.useMemo(() => {
    // 如果游戏已停止或空闲状态，剩余转数为0
    if (gameStatus === 'stopped' || gameStatus === 'idle') {
      return 0;
    }
    
    // 如果有游戏会话，优先使用后端传递的remainingTurns
    if (gameSession && gameSession.remainingTurns !== undefined) {
      const remaining = Math.max(0, gameSession.remainingTurns);
      console.log(`[剩余转数] 使用后端数据: remainingTurns=${remaining}, completedTurns=${gameSession.completedTurns}, selectedTurns=${gameSession.selectedTurns}`);
      return remaining;
    }
    
    // 如果有游戏会话但没有remainingTurns字段，手动计算
    if (gameSession && gameSession.selectedTurns && gameSession.completedTurns !== undefined) {
      const remaining = Math.max(0, gameSession.selectedTurns - gameSession.completedTurns);
      console.log(`[剩余转数] 手动计算: ${gameSession.selectedTurns} - ${gameSession.completedTurns} = ${remaining}`);
      return remaining;
    }
    
    // 如果没有游戏会话但游戏正在运行，显示选择的转数
    if (gameStatus === 'running') {
      console.log(`[剩余转数] 游戏运行中但无会话，显示选择转数: ${selectedTurns}`);
      return selectedTurns;
    }
    
    // 默认显示选择的转数（游戏开始前）
    return selectedTurns;
  }, [gameSession?.remainingTurns, gameSession?.selectedTurns, gameSession?.completedTurns, selectedTurns, gameStatus]);

  // 计算总可用资金（银币 + 积分）
  const totalAvailableFunds = React.useMemo(() => {
    return (user.silverCoins || 0) + (user.currentPoints || 0);
  }, [user.silverCoins, user.currentPoints]);

  // 每转消耗（从设备配置读取，默认 2.8）
  const turnCost = React.useMemo(() => {
    const v = device?.turnCost;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 2.8;
  }, [device?.turnCost]);

  // 当前显示的游戏数据：优先进行中的会话，其次上一次结果，最后用默认占位
  const displayGameData = React.useMemo(() => {
    if (gameSession) return gameSession;
    if (lastGameResult) return lastGameResult;
    return { selectedTurns, completedTurns: 0, earnedPoints: 0 };
  }, [gameSession, lastGameResult, selectedTurns]);

  const currentInvested = React.useMemo(() => {
    return displayGameData ? displayGameData.completedTurns * turnCost : 0;
  }, [displayGameData?.completedTurns, displayGameData, turnCost]);

  const netProfit = React.useMemo(() => {
    return displayGameData ? (displayGameData.earnedPoints || 0) - currentInvested : 0;
  }, [displayGameData?.earnedPoints, displayGameData, currentInvested]);

  const fetchDevice = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/devices/${deviceId}`);
      if (res.data && (res.data.type === 'sanqi' || res.data.modelType === 'sanqi' || res.data.modelType === '三七机')) {
        setDevice(res.data);
      } else {
        setDevice(null);
      }
    } catch (err) {
      setDevice(null);
    }
    setLoading(false);
  }, [deviceId]);

  const fetchGameStatus = React.useCallback(async () => {
    try {
      const res = await api.get(`/api/player/sanqi/status/${deviceId}`);
      console.log(`[fetchGameStatus] 服务器返回:`, res.data);
      
      if (res.data.hasGame) {
        console.log(`[fetchGameStatus] 设置游戏会话:`, res.data.session);
        setGameSession(res.data.session);
        setGameStatus(res.data.session.status);
      } else {
        console.log(`[fetchGameStatus] 无游戏会话，设置为idle`);
        setGameSession(null);
        setGameStatus('idle');
        // 不要清空lastGameResult，保留上一次游戏的结果
      }
    } catch (err) {
      console.error('获取游戏状态失败:', err);
    }
  }, [deviceId]);

  // 同步 video 静音状态，确保自动播放
  useEffect(() => {
    const vid = playerRef.current;
    if (!vid) return;
    try {
      vid.muted = isMuted;
      if (vid.autoplay || vid.getAttribute('autoplay') !== null) {
        const p = vid.play?.();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {/* ignore autoplay block */});
        }
      }
    } catch (_) {}
  }, [isMuted]);

  // 计算状态栏缩放，限制高度 <= 20% 视口高度，同时适配宽度
  useEffect(() => {
    const apply = () => {
      const el = statusBaseRef.current;
      if (!el) return;
      const vv = window.visualViewport;
      const vw = Math.min(window.innerWidth, document.documentElement.clientWidth || window.innerWidth);
      const rawVh = Math.min(window.innerHeight, document.documentElement.clientHeight || window.innerHeight);
      const vh = vv ? vv.height : rawVh;
      const maxH = Math.max(120, vh * 0.20);
      const baseW = el.scrollWidth || el.offsetWidth || 1200;
      const baseH = el.scrollHeight || el.offsetHeight || 240;
  const sidePad = 0; // 水平贴边：不再为左右预留边距
  const scaleW = (vw - sidePad) / baseW;
      const scaleH = maxH / baseH;
      const scale = Math.min(1, scaleW, scaleH);
      setStatusScale(scale);
      setStatusScaledH(Math.round(baseH * scale));
    };
    requestAnimationFrame(apply);
    const vv = window.visualViewport;
    window.addEventListener('resize', apply, { passive: true });
    window.addEventListener('orientationchange', apply, { passive: true });
    document.addEventListener('visibilitychange', apply, { passive: true });
    if (vv) vv.addEventListener('resize', apply, { passive: true });
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      document.removeEventListener('visibilitychange', apply);
      if (vv) vv.removeEventListener('resize', apply);
    };
  }, []);

  const toggleMute = () => {
    // 寻找真实在播的 video：可能是容器本身是 video，或容器内嵌了 video
    const el = document.getElementById('player-container-id');
    let v = null;
    if (el && el.tagName && el.tagName.toLowerCase() === 'video') v = el;
    else if (el && el.querySelector) v = el.querySelector('video');
    if (!v) v = playerRef.current;
    const next = !(v?.muted ?? isMuted);
    try { if (v) v.muted = next; } catch (_) {}
    // 在用户手势下，取消静音时主动触发播放，满足移动端音频策略
    if (v && next === false) {
      try { v.volume = 1; } catch (_) {}
      try { const p = v.play?.(); if (p && p.catch) p.catch(() => {}); } catch (_) {}
    }
    setIsMuted(next);
  };

  useEffect(() => {
    if (props.device) {
      setDevice(props.device);
      setLoading(false);
    } else {
      fetchDevice();
    }
    fetchGameStatus();
  }, [deviceId, props.device, fetchDevice, fetchGameStatus]);

  // —

  // 摇杆按键处理 - 就是个普通按键，只是做成摇杆的样子
  const handleJoystickStart = (e) => {
    e.preventDefault();
    
    // 简单的视觉反馈
    triggerButtonEffect('joystick');
    
    // 发送摇杆按键命令
    sendCommand('###laa', '摇杆按键');
    
    console.log('🕹️ 摇杆按键点击');
  };

  // 确认开始游戏（带参数版本，避免状态更新延迟）
  const handleConfirmStartWithTurns = async (turnsToUse) => {
    if (!device) {
      console.error('设备信息不存在');
      return;
    }
    
    console.log(`[确认开始] 使用传入的转数: ${turnsToUse}, 当前selectedTurns状态: ${selectedTurns}`);
    
    try {
      console.log('发送开始游戏请求:', {
        url: '/api/player/sanqi/start',
        deviceId: device._id,
        selectedTurns: turnsToUse,
        device: device,
        user: user
      });
      
      // 检查必要参数
      if (!device._id) {
        console.error('设备ID不存在:', device);
        return;
      }
      
      if (!turnsToUse || turnsToUse <= 0) {
        console.error('转数无效:', turnsToUse);
        return;
      }
      
      // 打印发送的数据
      const requestData = {
        deviceId: device._id,
        selectedTurns: turnsToUse
      };
      console.log('发送的请求数据:', JSON.stringify(requestData, null, 2));
      
      const res = await api.post('/api/player/sanqi/start', requestData);
      
      console.log('开始游戏响应:', res.data);
      
      if (res.data.success) {
        // 游戏开始成功
        setGameStatus('running');
        setLastGameResult(null); // 清除上一次游戏结果
        setShowTurnModal(false); // 关闭弹窗
        
        // 立即创建游戏会话，确保剩余转数计算正确
        console.log(`[三七机] 游戏开始成功，创建初始会话: selectedTurns=${turnsToUse}`);
        setGameSession({
          selectedTurns: turnsToUse,
          completedTurns: 0,
          earnedPoints: 0,
          status: 'running'
        });
        
        fetchGameStatus();
      } else {
        console.error('游戏开始失败:', res.data.message);
      }
    } catch (err) {
      console.error('开始游戏失败:', err);
      console.error('错误详情:', err.response?.data || err.message);
      
      // 显示更详细的错误信息
      if (err.response?.data) {
        console.error('服务器错误响应:', JSON.stringify(err.response.data, null, 2));
        if (err.response.data.message) {
          console.error('错误消息:', err.response.data.message);
        }
      }
    }
  };

  // 确认开始游戏
  const handleConfirmStart = async () => {
    if (!device) {
      console.error('设备信息不存在');
      return;
    }
    
    console.log(`[确认开始] 当前selectedTurns状态: ${selectedTurns}`);
    
    try {
      console.log('发送开始游戏请求:', {
        url: '/api/player/sanqi/start',
        deviceId: device._id,
        selectedTurns,
        device: device,
        user: user
      });
      
      // 检查必要参数
      if (!device._id) {
        console.error('设备ID不存在:', device);
        return;
      }
      
      if (!selectedTurns || selectedTurns <= 0) {
        console.error('转数无效:', selectedTurns);
        return;
      }
      
      // 打印发送的数据
      const requestData = {
        deviceId: device._id,
        selectedTurns: selectedTurns
      };
      console.log('发送的请求数据:', JSON.stringify(requestData, null, 2));
      
      const res = await api.post('/api/player/sanqi/start', requestData);
      
      console.log('开始游戏响应:', res.data);
      
      if (res.data.success) {
        // 游戏开始成功
        setGameStatus('running');
        setLastGameResult(null); // 清除上一次游戏结果
        setShowTurnModal(false); // 关闭弹窗
        
        // 立即创建游戏会话，确保剩余转数计算正确
        console.log(`[三七机] 游戏开始成功，创建初始会话: selectedTurns=${selectedTurns}`);
        setGameSession({
          selectedTurns: selectedTurns,
          completedTurns: 0,
          earnedPoints: 0,
          status: 'running'
        });
        
        fetchGameStatus();
      } else {
        console.error('游戏开始失败:', res.data.message);
      }
    } catch (err) {
      console.error('开始游戏失败:', err);
      console.error('错误详情:', err.response?.data || err.message);
      console.error('完整错误对象:', err);
      
      // 显示更详细的错误信息
      if (err.response?.data) {
        console.error('服务器错误响应:', JSON.stringify(err.response.data, null, 2));
        console.error('错误状态码:', err.response.status);
        console.error('错误状态文本:', err.response.statusText);
        
        // 如果有具体的错误消息，显示出来
        if (err.response.data.message) {
          console.error('错误消息:', err.response.data.message);
        }
        if (err.response.data.error) {
          console.error('错误信息:', err.response.data.error);
        }
        if (err.response.data.details) {
          console.error('错误详情:', err.response.data.details);
        }
      }
      
      // 检查是否是网络错误
      if (err.code === 'NETWORK_ERROR') {
        console.error('网络连接错误，请检查网络连接');
      }
      
      // 显示请求的详细信息，帮助调试
      console.error('请求配置:', {
        url: '/api/player/sanqi/start',
        method: 'POST',
        data: {
          deviceId: device._id,
          selectedTurns: selectedTurns
        },
        headers: err.config?.headers
      });
    }
  };

  // 停止游戏
  const handleStopGame = async () => {
    if (!device) {
      console.error('[三七机] 停止游戏失败：设备信息不存在');
      return;
    }
    
    // 防止重复调用
    if (isStoppingGame) {
      console.log('[三七机] 正在停止游戏中，忽略重复调用');
      return;
    }
    
    setIsStoppingGame(true);
    
    try {
      console.log('[三七机] 发送停止游戏请求');
      console.log('[三七机] 请求参数:', {
        deviceId: device._id,
        device: device,
        user: user
      });
      
      const requestData = {
        deviceId: device._id
      };
      console.log('[三七机] 发送的请求数据:', JSON.stringify(requestData, null, 2));
      
      const res = await api.post('/api/player/sanqi/stop', requestData);
      
      console.log('[三七机] 停止游戏响应:', res.data);
      
      if (res.data.success) {
        console.log('[三七机] 停止游戏请求成功，状态设置为stopping');
        setGameStatus('stopping');
        
        // 游戏停止后重置状态
        setTimeout(() => {
          console.log('[三七机] 游戏已停止，重置状态');
          setGameStatus('idle');
          setGameSession(null);
          setIsStoppingGame(false); // 重置停止标志
          // 不重置selectedTurns，保持用户的选择
        }, 1000);
      } else {
        console.error('[三七机] 停止游戏请求失败:', res.data.message);
        setIsStoppingGame(false); // 重置停止标志
      }
    } catch (err) {
      console.error('停止游戏失败:', err);
      console.error('错误详情:', err.response?.data || err.message);
      
      // 检查是否是游戏已经结束的情况
      if (err.response?.status === 400) {
        const errorMessage = err.response?.data?.message || '';
        if (errorMessage.includes('游戏已结束') || 
            errorMessage.includes('没有进行中的游戏') || 
            errorMessage.includes('游戏已经停止') ||
            errorMessage.includes('游戏未在运行状态')) {
          console.log('[三七机] 游戏已经自动结束，无需手动停止');
          // 游戏已经结束，重置前端状态
          setGameStatus('idle');
          setGameSession(null);
          setIsStoppingGame(false);
          return; // 不显示错误
        }
      }
      
      // 显示更详细的错误信息
      if (err.response?.data) {
        console.error('服务器错误响应:', JSON.stringify(err.response.data, null, 2));
        console.error('错误状态码:', err.response.status);
        console.error('错误状态文本:', err.response.statusText);
        
        if (err.response.data.message) {
          console.error('错误消息:', err.response.data.message);
        }
      }
      
      setIsStoppingGame(false); // 重置停止标志
    }
  };

  // 处理单个反馈的函数
  const processSingleResponse = React.useCallback((response, deviceId, originalData) => {
    console.log('🔵 [单个反馈] 解析后信息:');
    console.log('🔵 [单个反馈]   - 设备ID:', deviceId);
    console.log('🔵 [单个反馈]   - 反馈内容:', response);
    console.log('🔵 [单个反馈] 当前设备信息:');
    console.log('🔵 [单个反馈]   - device._id:', device?._id);
    console.log('🔵 [单个反馈]   - device.deviceId:', device?.deviceId);
    console.log('🔵 [单个反馈] 当前等待状态:');
    console.log('🔵 [单个反馈]   - pendingRelease:', pendingRelease ? JSON.stringify(pendingRelease, null, 2) : 'null');
    
    // 检查是否是当前设备的反馈
    const deviceMatches = (deviceId === device?._id || deviceId === device?.deviceId);
    console.log('🔵 [单个反馈] 设备匹配检查:');
    console.log('🔵 [单个反馈]   - 反馈设备ID:', deviceId);
    console.log('🔵 [单个反馈]   - 当前设备._id:', device?._id);
    console.log('🔵 [单个反馈]   - 当前设备.deviceId:', device?.deviceId);
    console.log('🔵 [单个反馈]   - 设备匹配结果:', deviceMatches);
    
    if (!deviceMatches) {
      console.log('🔴 [单个反馈] ❌ 反馈不是来自当前设备，忽略');
      return;
    }
    
    console.log('🟢 [单个反馈] ✅ 确认是当前设备的反馈');
    
    // 检查是否有等待松开的按键
    if (!pendingRelease) {
      console.log('🟡 [单个反馈] ❌ 没有等待松开的按键，忽略反馈');
      return;
    }

    console.log('🔵 [单个反馈] 📋 等待松开的按键详情:');
    console.log('🔵 [单个反馈]   - 按键标签:', pendingRelease.label);
    console.log('🔵 [单个反馈]   - 按下命令:', pendingRelease.pressCommand);
    console.log('🔵 [单个反馈]   - 期望反馈:', pendingRelease.expectResponse);
    console.log('🔵 [单个反馈]   - 松开命令:', pendingRelease.releaseCommand);
    console.log('🔵 [单个反馈]   - 实际反馈:', response);
    console.log('🔵 [单个反馈]   - 反馈匹配:', response === pendingRelease.expectResponse ? '✅ 匹配' : '❌ 不匹配');

    // 检测分数反馈（纯数字+ok格式）
    if (/^\d+ok$/.test(response)) {
      console.log('🟢 [单个反馈] 🎯 检测到分数反馈:', response);
    }

    // 检查反馈是否匹配当前等待的按键
    if (response === pendingRelease.expectResponse) {
      console.log(`🟢 [${pendingRelease.label}] ✅ 收到期望反馈 "${response}"，准备发送松开命令: ${pendingRelease.releaseCommand}`);
      
      // 发送松开命令
      const sendReleaseCommand = async () => {
        try {
          console.log(`🚀 [${pendingRelease.label}] 开始发送松开命令...`);
          console.log(`🚀 [${pendingRelease.label}] 松开命令详情:`);
          console.log(`🚀 [${pendingRelease.label}]   - deviceId: ${device._id}`);
          console.log(`🚀 [${pendingRelease.label}]   - command: ${pendingRelease.releaseCommand}`);
          console.log(`🚀 [${pendingRelease.label}]   - step: 1`);
          console.log(`🚀 [${pendingRelease.label}]   - expectedResponse: ok`);
          
          const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
          const res = await api.post(
            '/api/player/sanqi/manual-button',
            {
              deviceId: device._id,
              command: pendingRelease.releaseCommand,
              step: 1,
              expectedResponse: 'ok'
            },
            { withCredentials: true, headers }
          );

          if (res.data.success) {
            console.log(`🟢 [${pendingRelease.label}] ✅ 松开命令发送成功！响应:`, res.data);
          } else {
            console.error(`🔴 [${pendingRelease.label}] ❌ 松开命令发送失败:`, res.data.message);
          }
        } catch (err) {
          console.error(`🔴 [${pendingRelease.label}] ❌ 松开命令发送错误:`, err);
          console.error(`🔴 [${pendingRelease.label}] ❌ 错误详情:`, err.response?.data);
        }
      };

      // 立即发送松开命令并清除等待状态
      sendReleaseCommand();
      setPendingRelease(null);
      console.log(`🟢 [${pendingRelease.label}] 等待状态已清除`);
    } else {
      console.log(`🟡 [${pendingRelease.label}] 反馈不匹配:`);
      console.log(`🟡 [${pendingRelease.label}]   - 期望: "${pendingRelease.expectResponse}"`);
      console.log(`🟡 [${pendingRelease.label}]   - 实际: "${response}"`);
    }
  }, [device, pendingRelease]);

  // 监听游戏事件
  useEffect(() => {
    if (!socket) {
      console.log('🔴 [Socket] Socket未连接，无法监听事件');
      return;
    }

    console.log('🟢 [Socket] 开始设置事件监听器...');
    console.log('🟢 [Socket] Socket连接状态:', socket.connected);
    console.log('🟢 [Socket] Socket ID:', socket.id);

    const handleGameUpdate = (data) => {
      console.log('[三七机] 收到游戏更新:', data);
      setGameSession(prev => {
        console.log('[三七机] 更新前的会话:', prev);
        const updatedSession = prev ? { ...prev, ...data } : null;
        console.log('[三七机] 合并后的会话:', updatedSession);
        
        console.log(`[三七机] 当前会话状态:`, {
          有会话: !!updatedSession,
          已完成转数: updatedSession?.completedTurns,
          设定转数: updatedSession?.selectedTurns,
          状态: updatedSession?.status
        });
        
        // 检查是否达到设定转数，自动停止游戏
        if (updatedSession && updatedSession.completedTurns >= updatedSession.selectedTurns) {
          console.log(`[三七机] 🛑 已达到设定转数！已完成: ${updatedSession.completedTurns}/${updatedSession.selectedTurns}，准备自动停止游戏`);
          
          // 短暂延迟后停止游戏，确保当前轮次完成
          setTimeout(() => {
            // 重新获取最新的游戏会话状态，防止过期数据
            const currentGameSession = gameSession;
            
            // 使用多重检查，确保游戏确实需要停止且没有重复调用
            if (!isStoppingGame && 
                currentGameSession && 
                (gameStatus === 'running' || currentGameSession.status === 'running') && 
                currentGameSession.completedTurns >= currentGameSession.selectedTurns) {
              
              console.log('[三七机] 🛑 游戏更新触发：执行自动停止游戏', {
                gameStatus: gameStatus,
                sessionStatus: currentGameSession.status,
                已完成转数: currentGameSession.completedTurns,
                设定转数: currentGameSession.selectedTurns,
                isStoppingGame: isStoppingGame,
                条件: '所有条件满足，执行停止'
              });
              handleStopGame();
            } else {
              console.log('[三七机] ⚠️ 游戏状态检查失败，取消自动停止', {
                gameStatus: gameStatus,
                sessionStatus: currentGameSession?.status,
                已完成转数: currentGameSession?.completedTurns,
                设定转数: currentGameSession?.selectedTurns,
                转数条件满足: currentGameSession ? currentGameSession.completedTurns >= currentGameSession.selectedTurns : false,
                isStoppingGame: isStoppingGame,
                hasSession: !!currentGameSession
              });
            }
          }, 500); // 减少延迟，因为是游戏更新事件
        } else if (updatedSession) {
          console.log(`[三七机] ✅ 游戏更新：未达到设定转数，继续游戏: ${updatedSession.completedTurns}/${updatedSession.selectedTurns}`);
        }
        
        return updatedSession;
      });
      setGameStatus(data.status);
    };

    const handleRoundResult = (data) => {
      console.log('[三七机] 收到轮次结果:', data);
      // 更新游戏会话的获得积分
      setGameSession(prev => {
        const updatedSession = prev ? { ...prev, earnedPoints: data.totalEarned } : null;
        
        console.log(`[三七机] 轮次结果更新会话:`, {
          有会话: !!updatedSession,
          已完成转数: updatedSession?.completedTurns,
          设定转数: updatedSession?.selectedTurns,
          获得积分: data.totalEarned
        });
        
        // 在轮次结果中也检查是否达到设定转数
        if (updatedSession && updatedSession.completedTurns >= updatedSession.selectedTurns) {
          console.log(`[三七机] 🛑 轮次结果显示已达到设定转数！已完成: ${updatedSession.completedTurns}/${updatedSession.selectedTurns}，准备自动停止游戏`);
          
          // 轮次结果后立即停止游戏
          setTimeout(() => {
            // 重新获取最新的游戏会话状态，防止过期数据
            const currentGameSession = gameSession;
            
            // 使用多重检查，确保游戏确实需要停止且没有重复调用
            if (!isStoppingGame && 
                currentGameSession && 
                (gameStatus === 'running' || currentGameSession.status === 'running') && 
                currentGameSession.completedTurns >= currentGameSession.selectedTurns) {
              
              console.log('[三七机] 🛑 轮次结果触发：执行自动停止游戏', {
                gameStatus: gameStatus,
                sessionStatus: currentGameSession.status,
                已完成转数: currentGameSession.completedTurns,
                设定转数: currentGameSession.selectedTurns,
                isStoppingGame: isStoppingGame,
                条件: '所有条件满足，执行停止'
              });
              handleStopGame();
            } else {
              console.log('[三七机] ⚠️ 游戏状态检查失败，取消自动停止', {
                gameStatus: gameStatus,
                sessionStatus: currentGameSession?.status,
                已完成转数: currentGameSession?.completedTurns,
                设定转数: currentGameSession?.selectedTurns,
                转数条件满足: currentGameSession ? currentGameSession.completedTurns >= currentGameSession.selectedTurns : false,
                isStoppingGame: isStoppingGame,
                hasSession: !!currentGameSession
              });
            }
          }, 300); // 轮次结果后较短延迟
        } else if (updatedSession) {
          console.log(`[三七机] ✅ 轮次结果：未达到设定转数，继续游戏: ${updatedSession.completedTurns}/${updatedSession.selectedTurns}`);
        }
        
        return updatedSession;
      });
      
      // 轮次结果处理，不显示弹窗
    };

    const handleGameComplete = (data) => {
      console.log('[三七机] 游戏完成:', data);
      setGameStatus('stopped');
      const netResult = data.netResult;
      
      // 游戏完成，不显示弹窗
      
      // 保存游戏结果以便在空闲时显示
      if (gameSession) {
        setLastGameResult({
          ...gameSession,
          earnedPoints: data.totalEarned || gameSession.earnedPoints || 0,
          netResult: netResult
        });
      }
      
      setTimeout(() => {
        setGameSession(null);
        setGameStatus('idle');
      }, 3000);
    };

    const handlePointsUpdate = (data) => {
      console.log('[三七机] 收到积分更新:', data);
      // 实时更新用户积分（从AuthContext传递过来）
      // 这里不需要额外处理，因为AuthContext已经处理了
    };

    // 处理设备反馈 - 用于自动按下-松开逻辑，支持沾包处理
    const handleDeviceResponse = (data) => {
      console.log('🔵 [设备反馈] ==================== 收到设备反馈 ====================');
      console.log('🔵 [设备反馈] 原始数据:', JSON.stringify(data, null, 2));
      
      // 处理不同的数据格式：可能是 data.response 或 data.message
      const response = data.response || data.message;
      const deviceId = data.deviceId || data.tcpDeviceId;
      
      if (!response || !deviceId) {
        console.log('🔴 [设备反馈] ❌ 反馈数据不完整，忽略');
        return;
      }

      console.log('🔵 [设备反馈] 📦 检查是否存在沾包...');
      
      // 使用正则表达式检测和分割沾包数据
      // 匹配所有以字母数字开头、以"ok"结尾的完整反馈
      const responsePattern = /([a-zA-Z0-9]+ok)/g;
      const matches = response.match(responsePattern);
      
      if (matches && matches.length > 1) {
        console.log('🟡 [设备反馈] 🎯 检测到沾包数据！');
        console.log('🟡 [设备反馈] 原始消息:', response);
        console.log('🟡 [设备反馈] 分割结果:', matches);
        
        // 逐个处理每个分割出的反馈
        matches.forEach((singleResponse, index) => {
          console.log(`🔄 [设备反馈] 处理第 ${index + 1}/${matches.length} 个反馈: "${singleResponse}"`);
          processSingleResponse(singleResponse, deviceId, data);
        });
      } else if (matches && matches.length === 1) {
        console.log('🟢 [设备反馈] ✅ 单个反馈，正常处理');
        processSingleResponse(response, deviceId, data);
      } else {
        console.log('🟡 [设备反馈] ⚠️  反馈格式不匹配标准模式，尝试直接处理');
        processSingleResponse(response, deviceId, data);
      }
    };

    // 🔧 备用方案：监听通用设备消息事件（用于处理后端未正确转发的情况）
    const handleDeviceMessage = (data) => {
      console.log('🟠 [备用监听] 收到通用设备消息:', JSON.stringify(data, null, 2));
      
      // 尝试提取设备反馈信息
      let deviceId = data.deviceId || data.device || data.tcpDeviceId;
      let message = data.message || data.response || data.feedback;
      
      // 如果是我们期望的设备反馈格式，转发给设备反馈处理器
      if (deviceId && message) {
        console.log(`🟠 [备用监听] 转发给设备反馈处理器: deviceId=${deviceId}, message=${message}`);
        handleDeviceResponse({
          deviceId: deviceId,
          response: message,
          source: 'backup-listener'
        });
      }
    };

    socket.on('sanqiGameUpdate', handleGameUpdate);
    socket.on('sanqiRoundResult', handleRoundResult);
    socket.on('sanqiGameComplete', handleGameComplete);
    socket.on('pointsUpdate', handlePointsUpdate);
    socket.on('sanqiDeviceResponse', handleDeviceResponse); // 添加设备反馈监听
    
    // 🔧 添加备用事件监听器（用于处理后端未正确转发的情况）
    socket.on('deviceMessage', handleDeviceMessage);
    socket.on('deviceResponse', handleDeviceMessage);
    socket.on('tcpMessage', handleDeviceMessage);
    socket.on('deviceFeedback', handleDeviceMessage);
    
    console.log('🟢 [Socket] 事件监听器设置完成:');
    console.log('🟢 [Socket]   - sanqiGameUpdate: ✅');
    console.log('🟢 [Socket]   - sanqiRoundResult: ✅');
    console.log('🟢 [Socket]   - sanqiGameComplete: ✅');
    console.log('🟢 [Socket]   - pointsUpdate: ✅');
    console.log('🟢 [Socket]   - sanqiDeviceResponse: ✅ (关键：自动松开功能)');
    console.log('🟠 [Socket]   - deviceMessage: ✅ (备用)');
    console.log('🟠 [Socket]   - deviceResponse: ✅ (备用)');
    console.log('🟠 [Socket]   - tcpMessage: ✅ (备用)');
    console.log('🟠 [Socket]   - deviceFeedback: ✅ (备用)');

    return () => {
      socket.off('sanqiGameUpdate', handleGameUpdate);
      socket.off('sanqiRoundResult', handleRoundResult);
      socket.off('sanqiGameComplete', handleGameComplete);
      socket.off('pointsUpdate', handlePointsUpdate);
      socket.off('sanqiDeviceResponse', handleDeviceResponse); // 清理设备反馈监听器
      // 🔧 清理备用事件监听器
      socket.off('deviceMessage', handleDeviceMessage);
      socket.off('deviceResponse', handleDeviceMessage);
      socket.off('tcpMessage', handleDeviceMessage);
      socket.off('deviceFeedback', handleDeviceMessage);
    };
  }, [socket, pendingRelease, device, processSingleResponse, gameSession]);

    // 等比缩放（宽/高双约束）+ 视口实时测量 + 循环微调，确保任何移动端都能完整可见
    useEffect(() => {
      const el = consoleRef.current;
      if (!el) return;

      const getSafeBottom = () => {
        try {
          const tmp = getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom');
          if (tmp && tmp.includes('px')) return parseFloat(tmp);
        } catch (_) {}
        return 0;
      };

      const MARGIN_W = 6;  // 左右边距
      const MARGIN_H = 4;  // 底部额外间隙

      const measureBase = () => {
        // 不需要复位 transform，scrollWidth/offsetWidth 不受 transform 影响
        const BASE_W = Math.max(820, el.scrollWidth || el.offsetWidth || 820);
        const BASE_H = el.scrollHeight || el.offsetHeight || 220;
        return { BASE_W, BASE_H };
      };

      const apply = () => {
        const vv = window.visualViewport;
        const vw = Math.min(window.innerWidth, document.documentElement.clientWidth || window.innerWidth);
        const rawVh = Math.min(window.innerHeight, document.documentElement.clientHeight || window.innerHeight);
        const vh = vv ? vv.height : rawVh; // 适配移动工具条
        const { BASE_W, BASE_H } = measureBase();

        const safeBottom = getSafeBottom();
        const scaleW = Math.min(1, (vw - MARGIN_W * 2) / BASE_W);
        const availableH = vh - safeBottom - MARGIN_H;
        const scaleH = Math.min(1, availableH / BASE_H);

        const scale = Math.max(0.22, Math.min(scaleW, scaleH));
        setConsoleScale(scale);
      };

      // 初次与监听
      requestAnimationFrame(apply);
      const vv = window.visualViewport;
      window.addEventListener('resize', apply, { passive: true });
      window.addEventListener('orientationchange', apply, { passive: true });
      document.addEventListener('visibilitychange', apply, { passive: true });
      if (vv) vv.addEventListener('resize', apply, { passive: true });

      return () => {
        window.removeEventListener('resize', apply);
        window.removeEventListener('orientationchange', apply);
        document.removeEventListener('visibilitychange', apply);
        if (vv) vv.removeEventListener('resize', apply);
      };
    }, []);

    // 同步静音状态到可能的内层 video（TCPlayer生成的）
    useEffect(() => {
      const outer = playerRef.current;
      const inner = document.querySelector('#player-container-id video');
      try {
        if (outer) outer.muted = isMuted;
        if (inner) inner.muted = isMuted;
        if (inner && (inner.autoplay || inner.getAttribute('autoplay') !== null)) {
          const p = inner.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } catch (_) {}
    }, [isMuted]);
  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="text-center mt-5">
        <h3>设备不存在或不是三七机</h3>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          返回
        </Button>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#000', zIndex: 10 }}>
      {/* 添加自定义动画样式 */}
      <style>
        {`
          :root{ --safe-bottom: calc(env(safe-area-inset-bottom) + 6px); }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes buttonPulse {
            0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
            70% { box-shadow: 0 0 0 10px rgba(255, 0, 0, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
          }
          
          /* 移动端响应式基础样式 */
          @media (max-width: 768px) {
            .sanqi-mobile-optimized {
              padding: 5px !important;
            }
            
            .mobile-info-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 5px !important;
              padding: 5px !important;
            }
            
            .mobile-info-item {
              padding: 5px !important;
              font-size: 0.75rem !important;
            }
            
            .mobile-info-label {
              font-size: 0.6rem !important;
              margin-bottom: 1px !important;
            }
            
            .mobile-info-value {
              font-size: 0.8rem !important;
            }
          }
          
          /* 超小屏幕优化 */
          @media (max-width: 480px) {
            .mobile-info-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 3px !important;
            }
            
            .mobile-info-item {
              padding: 4px 6px !important;
            }
            
            .mobile-info-label {
              font-size: 0.55rem !important;
            }
            
            .mobile-info-value {
              font-size: 0.75rem !important;
            }
          }
          
          .arcade-control-panel {
            background: 
              radial-gradient(ellipse at 30% 20%, rgba(139, 69, 19, 0.1), transparent 70%),
              linear-gradient(135deg, #8B4513 0%, #A0522D 15%, #CD853F 30%, #D2B48C 45%, #A0522D 60%, #8B4513 80%, #654321 100%);
            border-radius: 20px;
            padding: 15px 25px;
            border: 2px solid #654321;
            box-shadow: 
              inset 0 2px 6px rgba(255,255,255,0.1),
              inset 0 -2px 6px rgba(0,0,0,0.3),
              0 4px 15px rgba(0,0,0,0.4);
            position: relative;
            /* 移动端响应式 */
            transition: all 0.3s ease;
          }
          
          /* 移动端控制台优化 */
          @media (max-width: 768px) {
            .arcade-control-panel {
              padding: 12px 20px !important;
              border-radius: 18px !important;
              margin: 0 10px !important;
              max-width: calc(100vw - 20px) !important;
              overflow-x: auto !important;
            }
          }
          
          @media (max-width: 480px) {
            .arcade-control-panel {
              padding: 10px 15px !important;
              border-radius: 15px !important;
              margin: 0 5px !important;
              gap: 6px !important;
            }
          }
          
          .arcade-control-panel::before {
            content: '';
            position: absolute;
            top: 3px;
            left: 3px;
            right: 3px;
            bottom: 3px;
            background: 
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 2px,
                rgba(0,0,0,0.1) 2px,
                rgba(0,0,0,0.1) 3px
              ),
              repeating-linear-gradient(
                0deg,
                transparent,
                transparent 1px,
                rgba(0,0,0,0.05) 1px,
                rgba(0,0,0,0.05) 2px
              );
            border-radius: 18px;
            pointer-events: none;
          }
          
          .arcade-button {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            border: 2px solid #333;
            position: relative;
            cursor: pointer;
            transition: all 0.1s ease;
            font-weight: bold;
            font-size: 10px;
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.8);
            margin: 0 3px;
            box-shadow: 
              0 4px 8px rgba(0,0,0,0.4),
              inset 0 2px 4px rgba(255,255,255,0.3),
              inset 0 -2px 4px rgba(0,0,0,0.3);
            /* 移动端触摸优化 */
            -webkit-tap-highlight-color: transparent;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          
          /* 移动端按钮尺寸优化 */
          @media (max-width: 768px) {
            .arcade-button {
              width: 55px !important;
              height: 55px !important;
              margin: 0 2px !important;
              font-size: 11px !important;
              /* 增大触摸目标 */
              min-width: 55px;
              min-height: 55px;
            }
          }
          
          @media (max-width: 480px) {
            .arcade-button {
              width: 50px !important;
              height: 50px !important;
              margin: 0 1px !important;
              font-size: 10px !important;
              min-width: 50px;
              min-height: 50px;
            }
          }
          
          /* 超小屏幕紧凑布局 */
          @media (max-width: 360px) {
            .arcade-button {
              width: 45px !important;
              height: 45px !important;
              font-size: 9px !important;
              min-width: 45px;
              min-height: 45px;
            }
          }
          
          /* 移动端触摸反馈增强 */
          @media (max-width: 768px) {
            .arcade-button:active,
            .arcade-button.pressed,
            .joystick-button:active,
            .joystick-button.pressed,
            .auto-arcade-button:active {
              transform: translateY(4px) scale(0.95) !important;
              transition: all 0.1s ease !important;
            }
          }
          
          /* 防止移动端缩放 */
          @media (max-width: 768px) {
            .arcade-console {
              -webkit-user-select: none;
              user-select: none;
              -webkit-touch-callout: none;
              -webkit-tap-highlight-color: transparent;
            }
          }
          
          .arcade-button:active,
          .arcade-button.pressed {
            transform: translateY(6px);
            box-shadow: 
              0 2px 8px rgba(0,0,0,0.4),
              inset 0 2px 6px rgba(0,0,0,0.6),
              inset 0 -1px 4px rgba(255,255,255,0.1);
          }
          
          .red-button {
            background: radial-gradient(circle at 30% 30%, #ff6b6b, #e53e3e, #c53030, #9c1c1c);
            border: 3px solid #8B0000;
          }
          
          .red-button:hover {
            background: radial-gradient(circle at 30% 30%, #ff7b7b, #f54e4e, #d53040, #ac2c2c);
            animation: buttonPulse 1.5s infinite;
          }
          
          .orange-button {
            background: radial-gradient(circle at 30% 30%, #ffa500, #ff8c00, #ff6b00, #e55100);
            border: 3px solid #CC4400;
          }
          
          .orange-button:hover {
            background: radial-gradient(circle at 30% 30%, #ffb520, #ff9c10, #ff7b10, #f56110);
          }
          
          .yellow-button {
            background: radial-gradient(circle at 30% 30%, #ffd700, #ffcc00, #ffb000, #e6a000);
            border: 3px solid #B8860B;
            color: #333;
            text-shadow: 0 1px 2px rgba(255,255,255,0.5);
          }
          
          .yellow-button:hover {
            background: radial-gradient(circle at 30% 30%, #ffe720, #ffdc10, #ffc010, #f6b010);
          }
          
          .green-button {
            background: radial-gradient(circle at 30% 30%, #48bb78, #38a169, #2f855a, #276749);
            border: 3px solid #1A5D1A;
          }
          
          .green-button:hover {
            background: radial-gradient(circle at 30% 30%, #58cb88, #48b179, #3f956a, #377759);
          }
          
          .blue-button {
            background: radial-gradient(circle at 30% 30%, #4299e1, #3182ce, #2b6cb0, #2c5282);
            border: 3px solid #1A365D;
          }
          
          .blue-button:hover {
            background: radial-gradient(circle at 30% 30%, #52a9f1, #4192de, #3b7cc0, #3c6292);
          }
          
          .joystick-button {
            width: 70px;
            height: 70px;
            background: #333;
            border: 2px solid #222;
            border-radius: 50%;
            position: relative;
            cursor: pointer;
            margin: 0 3px;
            box-shadow: 
              0 4px 8px rgba(0,0,0,0.4),
              inset 0 2px 4px rgba(255,255,255,0.1),
              inset 0 -2px 4px rgba(0,0,0,0.5);
            /* 移动端触摸优化 */
            -webkit-tap-highlight-color: transparent;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          
          /* 移动端摇杆尺寸优化 */
          @media (max-width: 768px) {
            .joystick-button {
              width: 75px !important;
              height: 75px !important;
              margin: 0 2px !important;
            }
            
            .joystick-base {
              width: 65px !important;
              height: 65px !important;
            }
            
            .joystick-stick {
              width: 28px !important;
              height: 28px !important;
            }
          }
          
          @media (max-width: 480px) {
            .joystick-button {
              width: 70px !important;
              height: 70px !important;
              margin: 0 1px !important;
            }
            
            .joystick-base {
              width: 60px !important;
              height: 60px !important;
            }
            
            .joystick-stick {
              width: 25px !important;
              height: 25px !important;
            }
          }
          
          @media (max-width: 360px) {
            .joystick-button {
              width: 65px !important;
              height: 65px !important;
            }
            
            .joystick-base {
              width: 55px !important;
              height: 55px !important;
            }
            
            .joystick-stick {
              width: 22px !important;
              height: 22px !important;
            }
          }
          
          .joystick-button:active,
          .joystick-button.pressed {
            transform: translateY(6px);
            box-shadow: 
              0 2px 8px rgba(0,0,0,0.4),
              inset 0 2px 6px rgba(0,0,0,0.6),
              inset 0 -1px 4px rgba(255,255,255,0.1);
          }
          
          .joystick-base {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 60px;
            height: 60px;
            background: #222;
            border-radius: 50%;
            border: 1px solid #111;
            box-shadow: inset 0 2px 6px rgba(0,0,0,0.8);
          }
          
          .joystick-stick {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 25px;
            height: 25px;
            background: radial-gradient(circle, #666 0%, #444 50%, #222 100%);
            border-radius: 50%;
            border: 1px solid #111;
            box-shadow: 
              0 2px 4px rgba(0,0,0,0.6),
              inset 0 1px 2px rgba(255,255,255,0.2);
            transform: translate(-50%, -50%);
          }
          
          .auto-arcade-button {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: radial-gradient(circle at 30% 30%, #dc2626, #b91c1c, #991b1b);
            border: 2px solid #333;
            color: white;
            font-weight: bold;
            font-size: 9px;
            text-shadow: 0 1px 2px rgba(0,0,0,0.8);
            cursor: pointer;
            transition: all 0.1s ease;
            margin: 0 3px;
            box-shadow: 
              0 4px 8px rgba(0,0,0,0.4),
              inset 0 2px 4px rgba(255,255,255,0.3),
              inset 0 -2px 4px rgba(0,0,0,0.3);
            /* 移动端触摸优化 */
            -webkit-tap-highlight-color: transparent;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          
          /* 移动端AUTO按钮优化 */
          @media (max-width: 768px) {
            .auto-arcade-button {
              width: 55px !important;
              height: 55px !important;
              margin: 0 2px !important;
              font-size: 10px !important;
              min-width: 55px;
              min-height: 55px;
            }
          }
          
          @media (max-width: 480px) {
            .auto-arcade-button {
              width: 50px !important;
              height: 50px !important;
              margin: 0 1px !important;
              font-size: 9px !important;
              min-width: 50px;
              min-height: 50px;
            }
          }
          
          @media (max-width: 360px) {
            .auto-arcade-button {
              width: 45px !important;
              height: 45px !important;
              font-size: 8px !important;
              min-width: 45px;
              min-height: 45px;
            }
          }
          
          .auto-arcade-button:hover {
            background: radial-gradient(circle at 30% 30%, #ef4444, #dc2626, #b91c1c);
          }
          
          .auto-arcade-button:active {
            transform: translateY(2px);
            box-shadow: 
              0 2px 4px rgba(0,0,0,0.5),
              inset 0 1px 3px rgba(0,0,0,0.4);
          }
          
          .button-highlight {
            position: absolute;
            top: 8px;
            left: 8px;
            right: 25px;
            height: 15px;
            background: linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
            border-radius: 50%;
            pointer-events: none;
          }
          /* ===== Image-style bottom console (transparent over video) ===== */
          .arcade-control-panel{background:transparent !important;border:none !important;box-shadow:none !important;padding:0 !important}
          .console-row{display:flex;align-items:flex-end;gap:12px;justify-content:flex-start}
          .btn-max{width:172px;height:86px;border-radius:22px;background:linear-gradient(#ff6b6b,#ff3434 37%,#d30c0c);box-shadow:inset 0 8px 0 #fff6, inset 0 -8px 0 #0006,0 16px 28px #0009;display:grid;place-items:center;border:2px solid #fff6;cursor:pointer;transition:transform .06s}
          .btn-max:active{transform:translateY(2px)}
          .btn-max span{font-weight:900;font-size:44px;letter-spacing:.04em;text-shadow:0 3px 0 #b81010,0 14px 22px #000a}
          .btn-push{width:232px;height:116px;border-radius:22px/38px;background:radial-gradient(circle at 50% 55%, #222 0 90px, #111 91px), #000;position:relative;display:grid;place-items:center;cursor:pointer;box-shadow:0 16px 28px #000a, inset 0 0 0 3px #000, inset 0 0 0 4px #704b12}
          .btn-push:before{content:"";position:absolute;inset:8px;border-radius:18px/30px;background:conic-gradient(from 0deg,#f6b44b,#8a5a14 20%,#f7e7a1 40%,#8a5a14 60%,#f6b44b 80%,#f7e7a1);mask:radial-gradient(circle at 50% 50%, transparent 54%, #000 56%);opacity:.9}
          .btn-push span{position:relative;font-size:44px;font-weight:900;color:#ff3b1f;text-shadow:0 0 22px #ff6b2b,0 6px 0 #000}
          .ovals{display:flex;gap:10px;margin-left:6px}
          .oval{width:56px;height:80px;border-radius:50px;background:linear-gradient(#f0f0f0,#c9c9c9);box-shadow:inset 0 2px 0 #fff6, inset 0 -6px 0 #0004,0 10px 20px #0007;display:grid;place-items:center;cursor:pointer}
          .oval .lamp{width:38px;height:54px;border-radius:40px;background:radial-gradient(circle at 50% 25%, #fff 0 14px, #b3f24e 15px 100%);display:grid;place-items:center}
          .oval .oval-label{font-weight:800;font-size:18px;color:#1a1a1a;text-shadow:0 1px 0 #fff8, 0 2px 4px #0003}
          .joy-ball{width:82px;height:82px;border-radius:999px;background:radial-gradient(circle at 35% 35%, #fff6, #2a2a2a 55%, #000 100%);box-shadow:inset -6px -10px 18px #0008,0 14px 26px #000a;border:1px solid #0009;transition:transform .12s ease}
          .joy-ball.pressed{transform:translateY(8px)}
          .dot-bar{flex:1;min-width:300px;height:104px;border-radius:26px;background:linear-gradient(#0b0e12,#0a0d11);box-shadow:inset 0 6px 10px #0009,0 16px 30px #000a;display:flex;align-items:center;justify-content:space-evenly;padding:0 16px}
          .dot-btn{width:74px;height:74px;border-radius:999px;background:radial-gradient(circle at 35% 35%, #fff, #eaeaea 40%, #c9c9c9 60%, #bdbdbd 100%);box-shadow:inset 0 -6px 10px #0003, 0 8px 18px #0008;border:1px solid #0006;cursor:pointer;transition:transform .06s}
          .dot-btn:active,.dot-btn.pressed{transform:translateY(2px)}

          /* Mobile size reduction for bottom console to avoid side clipping */
          @media (max-width: 768px) {
            .console-row{gap:10px}
            .btn-max{width:154px;height:76px}
            .btn-max span{font-size:38px}
            .btn-push{width:192px;height:96px}
            .btn-push span{font-size:36px}
            .ovals{gap:8px;margin-left:4px}
            .oval{width:50px;height:74px}
            .oval .lamp{width:34px;height:48px}
            .oval .oval-label{font-size:16px}
            .joy-ball{width:74px;height:74px}
            .dot-bar{min-width:270px;height:88px;padding:0 12px}
            .dot-btn{width:64px;height:64px}
          }
          @media (max-width: 480px) {
            .console-row{gap:8px}
            .btn-max{width:134px;height:66px}
            .btn-max span{font-size:32px}
            .btn-push{width:162px;height:82px}
            .btn-push span{font-size:30px}
            .ovals{gap:6px;margin-left:2px}
            .oval{width:44px;height:66px}
            .oval .lamp{width:30px;height:46px}
            .oval .oval-label{font-size:14px}
            .joy-ball{width:68px;height:68px}
            .dot-bar{min-width:230px;height:76px;padding:0 10px}
            .dot-btn{width:56px;height:56px}
          }
          @media (max-width: 360px) {
            .console-row{gap:6px}
            .btn-max{width:122px;height:60px}
            .btn-max span{font-size:28px}
            .btn-push{width:150px;height:74px}
            .btn-push span{font-size:26px}
            .ovals{gap:4px;margin-left:2px}
            .oval{width:40px;height:58px}
            .oval .lamp{width:26px;height:40px}
            .oval .oval-label{font-size:12px}
            .joy-ball{width:62px;height:62px}
            .dot-bar{min-width:210px;height:70px;padding:0 8px}
            .dot-btn{width:52px;height:52px}
          }
        `}
      </style>
      
      {/* 腾讯 WebRTC 播放器 */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1, background: '#000', pointerEvents: 'none' }}>
        {device.videoStreamAddress?.startsWith('webrtc://') ? (
          <div id="player-mount" style={{ width: '100%', height: '100%' }} />
        ) : null}
      </div>
      <TcPlayerInit
        streamUrl={device.videoStreamAddress}
        onLoading={() => setVideoReady(false)}
        onReady={() => setVideoReady(true)}
      />

      {/* 加载中全屏遮罩 */}
      {!videoReady && device.videoStreamAddress?.startsWith('webrtc://') && (
        <div style={{ position:'fixed', inset:0, zIndex: 5000, background:'#000', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ textAlign:'center', color:'#fff' }}>
            <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" style={{ display:'block', margin:'0 auto 18px' }}>
              <defs>
                {/* 原主品牌渐变（用于文字等） */}
                <linearGradient id="to-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00D1FF"/>
                  <stop offset="100%" stopColor="#7C4DFF"/>
                </linearGradient>
                {/* 彩虹旋转渐变：用于外围发光效果 */}
                <linearGradient id="to-rainbow" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="userSpaceOnUse">
                  <stop offset="0%"   stopColor="#ff0040"/>
                  <stop offset="16%"  stopColor="#ff7a00"/>
                  <stop offset="33%"  stopColor="#ffd400"/>
                  <stop offset="50%"  stopColor="#2bff00"/>
                  <stop offset="66%"  stopColor="#00d5ff"/>
                  <stop offset="83%"  stopColor="#6a00ff"/>
                  <stop offset="100%" stopColor="#ff00a8"/>
                  <animateTransform attributeName="gradientTransform" attributeType="XML" type="rotate" from="0 110 110" to="360 110 110" dur="6s" repeatCount="indefinite"/>
                </linearGradient>
                <radialGradient id="to-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.35)"/>
                  <stop offset="70%" stopColor="rgba(124,77,255,0.15)"/>
                  <stop offset="100%" stopColor="rgba(0,0,0,0)"/>
                </radialGradient>
                <filter id="to-neon" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
                  <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0  0 0 0 0 0.6  0 0 0 0 1  0 0 0 1 0" result="glow"/>
                  <feBlend in="SourceGraphic" in2="glow" mode="screen"/>
                </filter>
              </defs>
              <g style={{ transformOrigin:'110px 110px', animation:'to-spin 2.8s linear infinite' }}>
                <circle cx="110" cy="110" r="92" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8"/>
                {/* 外围彩虹光环（主环） */}
                <circle cx="110" cy="110" r="92" fill="none" stroke="url(#to-rainbow)" strokeWidth="8" strokeLinecap="round" strokeDasharray="560 220" className="to-dash" filter="url(#to-neon)"/>
                {/* 外围彩虹光晕（柔光加粗） */}
                <circle cx="110" cy="110" r="92" fill="none" stroke="url(#to-rainbow)" strokeWidth="14" opacity="0.25" filter="url(#to-neon)"/>
                <circle cx="110" cy="110" r="82" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
                <circle cx="110" cy="110" r="105" fill="none" stroke="rgba(124,77,255,0.08)" strokeWidth="1"/>
                {/* three orbiting dots */}
                <g style={{ transformOrigin:'110px 110px', animation:'to-spin 4.6s linear infinite' }}>
                  <circle cx="110" cy="14" r="4" fill="#00D1FF" filter="url(#to-neon)"/>
                </g>
                <g style={{ transformOrigin:'110px 110px', animation:'to-spin 6.2s linear infinite' }}>
                  <circle cx="110" cy="206" r="3.5" fill="#7C4DFF" filter="url(#to-neon)"/>
                </g>
                <g style={{ transformOrigin:'110px 110px', animation:'to-spin 3.2s linear infinite' }}>
                  <circle cx="200" cy="110" r="3" fill="#62F0FF" filter="url(#to-neon)"/>
                </g>
              </g>
              {/* inner glow */}
              <circle cx="110" cy="110" r="70" fill="url(#to-glow)" />
              {/* THE ONE text with neon */}
              <g filter="url(#to-neon)">
                <text x="110" y="118" textAnchor="middle" fontSize="28" fontWeight="900" letterSpacing="3" style={{ textTransform:'uppercase' }} fill="url(#to-grad)">THE ONE</text>
              </g>
            </svg>
            <div style={{ fontSize:16, letterSpacing:1.5, opacity:0.92 }}>THE ONE 欢迎您 · 视频加载中 · 请稍后</div>
          </div>
          <style>{`
            @keyframes to-spin { to { transform: rotate(360deg); } }
            @keyframes to-dash-kf { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -780; } }
            .to-dash { animation: to-dash-kf 2.2s linear infinite; }
          `}</style>
        </div>
      )}

      {/* 顶部信息区（1:1风格的三七状态栏） */}
      <div style={{
        margin: '0 auto',
        width: '100%',
        position: 'relative',
        zIndex: 20,
  // 顶部贴紧：仅保留安全区，去掉额外 8px 留白
  marginTop: 'env(safe-area-inset-top, 0px)',
  // 左右贴边：仅保留安全区
  paddingLeft: 'env(safe-area-inset-left, 0px)',
  paddingRight: 'env(safe-area-inset-right, 0px)',
        height: statusScaledH ?? 'auto'
      }}>
        <div
          ref={statusBaseRef}
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: `translateX(-50%) scale(${statusScale})`,
            transformOrigin: 'top center',
            width: 1200,
            pointerEvents: 'auto'
          }}
        >
          <SanqiStatusBoard
            deviceNo={device?.deviceId || device?.name || ''}
            username={user?.username || user?.name || ''}
            points={user?.currentPoints || 0}
            silver={user?.silverCoins || 0}
            // 差枚数 → 使用净差值（盈亏）：earnedPoints - completedTurns * turnCost
            score={netProfit || 0}
            // 剩余转数 → 使用 remainingTurns（后端优先，其次 selected - completed，再次选项值）
            difference={remainingTurns || 0}
            gridData={{
              columns: 12,
              rows: 8,
              // 用完成进度映射填充高度（0~rows）
              filled: Math.max(0, Math.min(8, Math.round(((displayGameData?.completedTurns || 0) / Math.max(1, displayGameData?.selectedTurns || 1)) * 8)))
            }}
            clock={clockText}
          />
        </div>
        {/* 放在顶部状态栏（信息面板）下方 60px，靠右边缘（白色图标） */}
  <div className="sanqi-mute-adjust" style={{
          position: 'absolute',
          top: 'calc(100% + 48px)',
          right: 0,
          zIndex: 1012,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 8
        }}>
          {isMuted ? (
            <IoVolumeMute size={28} onClick={toggleMute} title="取消静音" style={{ cursor: 'pointer', color: '#fff' }} />
          ) : (
            <IoVolumeHigh size={28} onClick={toggleMute} title="静音" style={{ cursor: 'pointer', color: '#fff' }} />
          )}
        </div>
      </div>

      {/* 移动端：再微调静音按钮与状态栏的间距 */}
      <style>
        {`
          @media (max-width: 768px) {
            .sanqi-mute-adjust { top: calc(100% + 20px) !important; }
          }
        `}
      </style>

      {/* 控制台按键区域 */}
      <div
        className="arcade-control-panel"
        ref={consoleRef}
        style={{ 
          position:'absolute',
          bottom:'var(--safe-bottom)',
          left:'50%',
          transform:`translateX(-50%) scale(${consoleScale})`,
          transformOrigin:'bottom center',
          zIndex:30,
          width:820,                 // 固定设计宽度，内部相对位置不变
          display:'flex',
          flexDirection:'column',
          justifyContent:'center',
          alignItems:'center',
          gap:0,
          margin:'0 auto',
          flexWrap:'nowrap',
          overflow:'visible',
          whiteSpace:'nowrap'
        }}
      >
        <div className="console-row">
          <div className={`btn-max ${pressedButtons.max ? 'pressed' : ''}`} onClick={handleMaxPress}><span>MAX</span></div>
          <div className="btn-push" onMouseDown={handleButton2Press}><span>push</span></div>
          <div className="ovals">
            <div
              className="oval"
              onClick={() => {
                if (gameStatus === 'running' || gameSession?.status === 'running') {
                  handleStopGame();
                } else {
                  setShowTurnModal(true);
                }
              }}
              title={gameStatus === 'running' || gameSession?.status === 'running' ? '停止' : '自动'}
            >
              <div className="lamp"><span className="oval-label">{(gameStatus === 'running' || gameSession?.status === 'running') ? '停止' : '自动'}</span></div>
            </div>
          </div>
        </div>
        <div className="console-row" style={{marginTop:8}}>
          <div className={`joy-ball ${pressedButtons.joystick ? 'pressed' : ''}`} onClick={handleJoystickStart} />
          <div className="dot-bar">
            <div className={`dot-btn ${pressedButtons.button1 ? 'pressed' : ''}`} onClick={handleButton1Press} />
            <div className={`dot-btn ${pressedButtons.button2 ? 'pressed' : ''}`} onClick={handleButton2Press} />
            <div className={`dot-btn ${pressedButtons.button3 ? 'pressed' : ''}`} onClick={handleButton3Press} />
          </div>
        </div>
      </div>

      {/* 转数选择弹窗 */}
      <TurnModal
        show={showTurnModal}
        onHide={() => setShowTurnModal(false)}
        options={TURN_OPTIONS}
        initialSelected={selectedTurns}
        turnCost={turnCost}
        userPoints={totalAvailableFunds}
        onConfirm={value => {
          console.log(`[TurnModal] 用户确认转数: ${value}, 当前selectedTurns: ${selectedTurns}`);
          setSelectedTurns(value);
          console.log(`[TurnModal] 设置新的selectedTurns: ${value}`);
          setShowTurnModal(false);
          
          // 使用传入的value而不是selectedTurns状态，避免状态更新延迟
          setTimeout(() => {
            console.log(`[TurnModal] 延迟后开始游戏，使用转数: ${value}`);
            handleConfirmStartWithTurns(value);
          }, 100);
        }}
      />

      {/* 状态显示区域 */}
      <div className="status-wrapper" style={{ 
        position: 'absolute',
        bottom: 'calc(var(--safe-bottom) + 132px)',// 在控制台上方
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 29,
        width: 'fit-content',
        maxWidth: 'calc(100vw - 20px)', // 移动端防止溢出
        padding: '0 10px'
      }}>
        <div style={{
          marginTop: '25px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(20,20,20,0.3) 100%)',
          borderRadius: '15px',
          padding: '10px 18px',
          border: '2px solid rgba(255,255,255,0.1)',
          boxShadow: 'inset 0 3px 8px rgba(0,0,0,0.3), 0 2px 10px rgba(138,43,226,0.2)',
          display: (gameStatus === 'stopping' || (gameStatus === 'idle' && totalAvailableFunds < selectedTurns * turnCost)) ? 'block' : 'none',
          /* 移动端优化 */
          fontSize: '14px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          <div style={{
            /* 移动端文字大小调整 */
            fontSize: window.innerWidth <= 480 ? '12px' : '14px'
          }}>
            {gameStatus === 'stopping' && (
              <span style={{ color: '#ff6600', fontSize: 'inherit', fontWeight: 'bold' }}>
                ⏸️ 正在停止游戏，请等待...
              </span>
            )}
            {gameStatus === 'idle' && totalAvailableFunds < selectedTurns * turnCost && (
              <span style={{ color: '#ff4444', fontSize: 'inherit', fontWeight: 'bold' }}>
                ❌ 资金不足，需要 {(selectedTurns * turnCost).toFixed(1)} (银币+积分)
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// 腾讯云 WebRTC 播放器初始化组件
function TcPlayerInit({ streamUrl, onLoading, onReady }) {
  const shouldInit = !!streamUrl && streamUrl.startsWith('webrtc://');
  const loadCSS = (href) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) { resolve(); return; }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  useEffect(() => {
  if (!shouldInit) return;
    let player;
    let destroyed = false;
    let reconnectTimer = null;
    let watchdogTimer = null;
  let startupTimeout = null;
    let backoff = 1200; // 初始重连间隔
    let lastTime = 0;
    let stalledTicks = 0;
    let userMutedPref = true; // 记录用户静音偏好，默认与初始 autoplay 策略一致
  let reportedReady = false;
  const markLoading = () => { try { onLoading && onLoading(); } catch(_){} };
  const markReady = () => { if (!reportedReady) { reportedReady = true; try { onReady && onReady(); } catch(_){} } };

    const clearTimers = () => {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (watchdogTimer) { clearInterval(watchdogTimer); watchdogTimer = null; }
      if (startupTimeout) { clearTimeout(startupTimeout); startupTimeout = null; }
    };

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.body.appendChild(s);
      });

    const getVideoEl = () => {
      // 兼容：player-container-id 可能是 video 本身，或其内部存在真实 <video>
      const host = document.getElementById('player-container-id');
      if (host) {
        if (host.tagName && host.tagName.toLowerCase() === 'video') return host;
        const inner = host.querySelector ? host.querySelector('video') : null;
        if (inner) return inner;
      }
      const mount = document.getElementById('player-mount');
      if (mount && mount.querySelector) return mount.querySelector('video');
      return null;
    };
    const tryPlay = (preferMutedFallback = true) => {
      const v = getVideoEl();
      if (!v) return;
      try {
        const p = v.play?.();
        if (p && typeof p.catch === 'function') {
          p.catch((err) => {
            // 若因为手势限制失败，且允许回落，则强制静音后重试一次
            if (preferMutedFallback) {
              v.muted = true;
              userMutedPref = true;
              try {
                const p2 = v.play?.();
                if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
              } catch (_) {}
            }
          });
        }
      } catch (_) {}
    };
    const ensurePlay = () => {
      const v = getVideoEl();
      if (!v) return;
      // 使用记录的偏好，不主动覆盖用户设置
      if (typeof userMutedPref === 'boolean') v.muted = userMutedPref;
      tryPlay(true);
    };

  const startWatchdog = () => {
      clearTimers();
      watchdogTimer = setInterval(() => {
        if (destroyed) return;
    const v = getVideoEl();
        if (!v) return;
        // readyState < 2 基本不可播放；或时间不前进视为卡顿
        const t = v.currentTime || 0;
        const advanced = t > lastTime + 0.05; // 50ms 容差
        const readyOk = (v.readyState || 0) >= 2;
        if (!advanced || !readyOk) {
          stalledTicks += 1;
        } else {
          stalledTicks = 0;
          // 首次检测到前进且 readyOk，认为已可播放
          markReady();
        }
        lastTime = t;
        if (stalledTicks >= 3) { // 约 3*2s = 6s 无前进则重连
          console.warn('[TCPlayer][sanqi] stalled detected, reconnecting...');
          reconnect('watchdog-stalled');
        }
      }, 2000);
    };

    const destroyPlayer = () => {
      // 先读取当前的视频静音状态以便重建时恢复
      try {
        const v = getVideoEl();
        if (v && typeof v.muted === 'boolean') userMutedPref = v.muted;
      } catch (_) {}
      try { player?.pause?.(); } catch (_) {}
      try { player?.destroy?.(); } catch (_) {}
      player = null;
      // 移除宿主节点，避免二次进入时残留 DOM 干扰 SDK 初始化
      try {
        const host = document.getElementById('player-container-id');
        if (host && host.parentNode) host.parentNode.removeChild(host);
      } catch (_) {}
    };

    const reconnect = (reason) => {
      if (destroyed) return;
      clearTimers();
      destroyPlayer();
      reportedReady = false;
      markLoading();
      const delay = Math.min(backoff, 15000);
      console.warn('[TCPlayer][sanqi] reconnect due to:', reason, 'in', delay, 'ms');
      reconnectTimer = setTimeout(() => {
        if (destroyed) return;
        backoff = Math.min(Math.floor(backoff * 1.6), 20000);
        initPlayer();
      }, delay);
    };

    const bindPlayerEvents = () => {
      try {
        player?.on?.('error', (e) => {
          console.warn('[TCPlayer][sanqi] error event', e);
          reconnect('error');
        });
        player?.on?.('waiting', () => {
          // 等待缓冲，短暂观察由 watchdog 处理
        });
        player?.on?.('play', () => {
          backoff = 1200; // 成功后重置退避
          ensurePlay();
          markReady();
          if (startupTimeout) { clearTimeout(startupTimeout); startupTimeout = null; }
        });
        player?.on?.('loadeddata', () => {
          ensurePlay();
          // 有些端上先触发 loadeddata 再 playing
          const v = getVideoEl();
          if (v && v.readyState >= 2) {
            markReady();
            if (startupTimeout) { clearTimeout(startupTimeout); startupTimeout = null; }
          }
        });
        player?.on?.('ended', () => {
          reconnect('ended');
        });
      } catch (e) {
        // 忽略
      }
    };

  const initPlayer = async () => {
      try {
        const mountEl = document.getElementById('player-mount');
        if (!mountEl || destroyed) return;
    markLoading();
        // 若还没有宿主 video，则动态创建，避免 React 直接管理 video 节点
        let host = document.getElementById('player-container-id');
        if (!host) {
          host = document.createElement('video');
          host.id = 'player-container-id';
          host.className = 'tcplayer';
          host.setAttribute('preload', 'auto');
          host.setAttribute('playsinline', 'true');
          host.setAttribute('webkit-playsinline', 'true');
          host.style.width = '100%';
          host.style.height = '100%';
          host.style.objectFit = 'cover';
          host.style.background = '#000';
          // 允许初次点击触发播放（满足部分端要求“直接点击视频”）
          host.style.pointerEvents = 'auto';
          mountEl.appendChild(host);
        }
        await loadCSS('https://web.sdk.qcloud.com/player/tcplayer/release/v4.9.1/tcplayer.min.css');
        // 先主源，失败后换备用域名
        try {
          await loadScript('https://video.sdk.qcloudecdn.com/web/TXLivePlayer-1.3.2.min.js');
        } catch (_) {
          await loadScript('https://web.sdk.qcloud.com/player/tcplayer/release/TXLivePlayer-1.3.2.min.js');
        }
        await loadScript('https://web.sdk.qcloud.com/player/tcplayer/release/v4.9.1/tcplayer.v4.9.1.min.js');
        if (destroyed || !window.TCPlayer) return;
        // WebRTC 不再追加 cache-bust，避免个别端对 URL 识别异常
        player = new window.TCPlayer('player-container-id', {
          sources: [streamUrl],
          autoplay: true,
          controls: false,
          live: true,
          playsinline: true
        });
  // 恢复用户静音偏好
  const v = getVideoEl();
  if (v && typeof userMutedPref === 'boolean') v.muted = userMutedPref;
  // 启动超时：若 N 秒未 ready，触发一次重连以提升首连成功率
  startupTimeout = setTimeout(() => {
    if (!destroyed && !reportedReady) reconnect('startup-timeout');
  }, 8000);
  bindPlayerEvents();
  ensurePlay();
        startWatchdog();
      } catch (e) {
        console.warn('[TCPlayer][sanqi] init failed, schedule reconnect', e);
        reconnect('init-failed');
      }
    };

  const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // 页面回到前台，拉起播放或重连
    const v = getVideoEl();
        if (!v || v.readyState < 2) {
          reconnect('visibility-visible');
        } else {
          ensurePlay();
        }
      }
    };
    const onOnline = () => reconnect('online');
    const onClickPlay = () => ensurePlay(); // 用户手势触发

    initPlayer();
    // 过滤浏览器对 play() 中止的 AbortError，避免用户端错误面板
    const onUR = (e) => {
      const reason = e?.reason;
      const msg = (reason && (reason.message || reason.toString?.())) || '';
      const name = reason && reason.name;
      if (name === 'AbortError' || (typeof msg === 'string' && msg.includes('The operation was aborted'))) {
        e.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', onUR);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onVisibility);
    window.addEventListener('click', onClickPlay, { once: true, capture: true });

    return () => {
      destroyed = true;
      clearTimers();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onVisibility);
  window.removeEventListener('click', onClickPlay, true);
  window.removeEventListener('unhandledrejection', onUR);
      destroyPlayer();
    };
  }, [streamUrl, shouldInit]);
  return null;
}

export default SanqiDeviceDetail;
